// Receipt / invoice parser — pulls structured fields out of an OCR result.
// Heuristics-only (no ML); designed for common Western, Russian and EU layouts.
// Exposes: window.OcrReceipt = { parse(result) }
(function () {
  'use strict';

  // Words that strongly imply the *next* numeric token is the line total.
  const TOTAL_KEYWORDS = [
    'total', 'итого', 'итог', 'к оплате', 'сумма', 'amount due', 'amount',
    'grand total', 'subtotal', 'gesamt', 'summe', 'totale', 'totaal',
    'total a pagar', 'total à payer', '합계', '총액', 'الإجمالي', 'المجموع',
    'jumlah', 'total geral',
  ];
  const SUBTOTAL_KEYWORDS = ['subtotal', 'sub-total', 'промежуточный', 'zwischensumme', 'sous-total'];
  const TAX_KEYWORDS = ['tax', 'vat', 'gst', 'ндс', 'mwst', 'tva', 'iva', 'btw', '부가가치세', 'ضريبة'];
  const DATE_KEYWORDS = ['date', 'дата', 'datum', 'fecha', 'data', 'tarih', '日付', '날짜', 'تاريخ', 'tanggal', 'jour'];

  // Currency symbols + ISO codes seen in the line where a total lives.
  const CURRENCY_RE = /([$€£¥₽₩₴₸฿₺₪₦₱])|(USD|EUR|GBP|JPY|RUB|CNY|KRW|INR|BRL|UAH|TRY|IDR|AUD|CAD|CHF|SEK|NOK|DKK|PLN|CZK|HUF|MXN|ZAR|SGD|HKD|AED|SAR|ILS|THB|MYR|VND|TWD|NZD|RON|BGN)\b/i;

  // Decimal numbers — handle 1,234.56 and 1.234,56 and 1234,56 and 1234.56
  const NUMBER_RE = /(?:\d{1,3}(?:[,.\s]\d{3})+|\d+)(?:[.,]\d{2})/;

  // Date patterns — ISO, EU dot, EU slash, US slash, written month
  const DATE_PATTERNS = [
    /\b(20\d{2}|19\d{2})[-./](0[1-9]|1[0-2])[-./](0[1-9]|[12]\d|3[01])\b/,
    /\b(0[1-9]|[12]\d|3[01])[-./](0[1-9]|1[0-2])[-./](20\d{2}|19\d{2}|\d{2})\b/,
    /\b(0[1-9]|1[0-2])[-./](0[1-9]|[12]\d|3[01])[-./](20\d{2}|19\d{2}|\d{2})\b/,
    /\b(0?[1-9]|[12]\d|3[01])\s+(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec|января|февраля|марта|апреля|мая|июня|июля|августа|сентября|октября|ноября|декабря)[a-zа-я.]*\s+(20\d{2}|19\d{2}|\d{2})/i,
  ];

  function normaliseNumber(raw) {
    // Strip currency symbols/codes; pick the best decimal interpretation.
    let s = String(raw).replace(/[^\d,.\s]/g, '').trim();
    if (!s) return NaN;
    // 1,234.56  → US: comma=thousands, dot=decimal
    // 1.234,56  → EU: dot=thousands, comma=decimal
    // 1234,56   → EU: comma=decimal
    // 1234.56   → universal
    const lastComma = s.lastIndexOf(',');
    const lastDot = s.lastIndexOf('.');
    if (lastComma > -1 && lastDot > -1) {
      if (lastDot > lastComma) {
        // dot is decimal
        s = s.replace(/,/g, '').replace(/\s/g, '');
      } else {
        s = s.replace(/\./g, '').replace(/\s/g, '').replace(',', '.');
      }
    } else if (lastComma > -1) {
      // Only comma — assume decimal if it leaves exactly 2 trailing digits
      const tail = s.length - lastComma - 1;
      if (tail === 2) s = s.replace(/\./g, '').replace(/\s/g, '').replace(',', '.');
      else s = s.replace(/,/g, '').replace(/\s/g, '');
    } else {
      s = s.replace(/\s/g, '');
    }
    const n = parseFloat(s);
    return isFinite(n) ? n : NaN;
  }

  function findCurrency(text) {
    const m = text.match(CURRENCY_RE);
    if (!m) return null;
    const sym = m[1] || m[2];
    const map = { '$': 'USD', '€': 'EUR', '£': 'GBP', '¥': 'JPY', '₽': 'RUB', '₩': 'KRW',
                  '₴': 'UAH', '₸': 'KZT', '฿': 'THB', '₺': 'TRY', '₪': 'ILS', '₦': 'NGN', '₱': 'PHP' };
    return (sym in map) ? map[sym] : sym.toUpperCase();
  }

  // Group words into lines (same as ocr-export.js groupLines, but inline so the
  // parser can run without that module loaded — receipt-mode is best-effort)
  function groupLines(words) {
    if (!words || !words.length) return [];
    const valid = words.filter(w => w.bbox && w.text);
    if (!valid.length) return [];
    const sorted = valid.slice().sort((a, b) => a.bbox.y0 - b.bbox.y0);
    const lines = [];
    let current = [], top = sorted[0].bbox.y0;
    const tol = 8;
    for (const w of sorted) {
      if (!current.length || Math.abs(w.bbox.y0 - top) <= tol) {
        current.push(w);
        top = current.reduce((a, b) => a + b.bbox.y0, 0) / current.length;
      } else { lines.push(current); current = [w]; top = w.bbox.y0; }
    }
    if (current.length) lines.push(current);
    return lines.map(ws => {
      ws.sort((a, b) => a.bbox.x0 - b.bbox.x0);
      return { words: ws, text: ws.map(w => w.text).join(' '), y: ws[0].bbox.y0 };
    });
  }

  function parseTotal(lines) {
    // Score each line: presence of total-keyword + a numeric token wins.
    let best = null, bestScore = -Infinity;
    for (const line of lines) {
      const lower = line.text.toLowerCase();
      const isSubtotal = SUBTOTAL_KEYWORDS.some(k => lower.includes(k));
      const isTax = TAX_KEYWORDS.some(k => lower.includes(k));
      const hasTotal = TOTAL_KEYWORDS.some(k => lower.includes(k));
      const m = line.text.match(NUMBER_RE);
      if (!m) continue;
      let score = 0;
      if (hasTotal) score += 4;
      if (isSubtotal) score -= 2;
      if (isTax) score -= 3;
      // Prefer lines closer to the bottom (totals usually at end)
      score += (line.y / 1000);
      if (score > bestScore) {
        bestScore = score;
        best = { value: normaliseNumber(m[0]), raw: m[0], line: line.text };
      }
    }
    if (best && isFinite(best.value)) return best;
    return null;
  }

  function parseDate(text) {
    for (const pat of DATE_PATTERNS) {
      const m = text.match(pat);
      if (m) return m[0];
    }
    return null;
  }

  function parseVendor(lines, totalLine) {
    // Heuristic: vendor is usually the largest-font OR all-caps line in the
    // top of the receipt. Prefer first 35% of lines; emphasise font-height
    // over raw length so big bold names beat addresses.
    if (!lines.length) return null;
    const top = lines.slice(0, Math.max(4, Math.floor(lines.length * 0.35)));
    // First pass: compute median line height across the WHOLE receipt so we
    // can identify "abnormally large" lines (these are the candidates).
    const allH = [];
    for (const l of lines) {
      const hs = l.words.map(w => w.bbox.y1 - w.bbox.y0).filter(h => h > 0);
      if (hs.length) allH.push(hs.reduce((a, b) => a + b, 0) / hs.length);
    }
    allH.sort((a, b) => a - b);
    const medianH = allH.length ? allH[Math.floor(allH.length / 2)] : 0;

    let best = null, bestScore = 0;
    // Common patterns we should NEVER mistake for a vendor name.
    const NON_VENDOR_PATTERNS = [
      /^\d/,                                   // street numbers, ZIPs, etc.
      /^(tel|phone|fax|ph|email|web|addr|address|date|order|invoice|receipt|tax\s?id|gst|vat|ein)\b/i,
      /^[\s\d.,\-+()]+$/,                      // numbers + punctuation only
      /^(www\.|http)/i,
    ];
    for (const line of top) {
      const t = line.text.trim();
      if (!t || t.length < 3) continue;
      const alphaCount = (t.match(/[A-Za-zА-Яа-яЁё]/g) || []).length;
      if (alphaCount < 3) continue;
      if (NON_VENDOR_PATTERNS.some(p => p.test(t))) continue;
      // Lines that contain a comma followed by 1-2 word tokens (e.g.
      // "123 Oak Street, Springfield") are address lines, not vendors.
      if (/^[A-Z][a-z]+\s+(St|Street|Ave|Avenue|Rd|Road|Blvd|Lane|Ln|Way|Dr|Drive)\b/i.test(t)) continue;
      if (totalLine && t === totalLine) continue;
      const heights = line.words.map(w => w.bbox.y1 - w.bbox.y0).filter(h => h > 0);
      const avgH = heights.length ? heights.reduce((a, b) => a + b, 0) / heights.length : 0;
      // All-caps short tokens are common in vendor names — bonus.
      const isUpper = /^[\sA-Z0-9.,&'\-]+$/.test(t) && /[A-Z]{2,}/.test(t);
      const heightRatio = medianH > 0 ? avgH / medianH : 1;
      const score =
        t.length * 0.25 +              // length component (small weight)
        avgH * 1.6 +                    // font-height (primary)
        (heightRatio > 1.3 ? 12 : 0) +  // big-font bonus
        (isUpper ? 8 : 0);              // ALL-CAPS bonus
      if (score > bestScore) { bestScore = score; best = t; }
    }
    return best;
  }

  function parseLineItems(lines) {
    // Each item line: text + price-looking token at the end.
    // Price MUST have decimal+2 digits (e.g. 3.50) to filter out phone numbers
    // (555-0142), order IDs (2026-451), dates (2026-05-08) and ZIP codes.
    const out = [];
    const PRICE_AT_END_RE = /([$€£¥₽₩]?\s*[\d,.\s]*\d[.,]\d{2})\s*$/;
    for (const line of lines) {
      const t = line.text;
      const m = t.match(PRICE_AT_END_RE);
      if (!m) continue;
      const num = normaliseNumber(m[1]);
      if (!isFinite(num)) continue;
      // Reject implausible values — tip jars don't ring up at $99,999
      if (num < 0.01 || num > 100000) continue;
      const desc = t.slice(0, t.length - m[0].length).trim().replace(/[:\-]+$/, '');
      if (!desc || desc.length < 3) continue;
      const lower = desc.toLowerCase();
      // Skip subtotal / total / tax — these are summary fields, not items.
      if (TOTAL_KEYWORDS.some(k => lower === k || lower.endsWith(' ' + k))) continue;
      if (SUBTOTAL_KEYWORDS.some(k => lower.includes(k))) continue;
      if (TAX_KEYWORDS.some(k => lower.includes(k))) continue;
      // Skip date-, phone-, order-prefixed lines.
      if (/^(date|tel|phone|order|invoice|receipt|fax|ph|tax\s?id)\b/i.test(desc)) continue;
      out.push({ description: desc, price: num });
    }
    return out.slice(0, 50);
  }

  function parse(result) {
    if (!result) return null;
    const text = result.text || '';
    const lines = groupLines(result.words || []);
    const total = parseTotal(lines);
    const date = parseDate(text);
    const vendor = parseVendor(lines, total ? total.line : null);
    const items = parseLineItems(lines);
    const currency = findCurrency(text);
    if (!total && !date && !vendor) return null;
    return {
      total: total ? total.value : null,
      total_raw: total ? total.raw : null,
      currency,
      date,
      vendor,
      items: items.length ? items : null,
    };
  }

  window.OcrReceipt = { parse, normaliseNumber, parseDate, parseTotal, parseVendor };
})();
